import { useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  useOutsideClick,
  VStack,
} from "@chakra-ui/react";
import { CaretDown } from "@phosphor-icons/react";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { Paginator } from "components/Paginator";

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50];

// Shared header-cell look used across the platform's data tables (e.g.
// QuarterlyBreakdownTable, ScopeOneDetail) — light gray band, muted label text.
const HEADER_CELL_STYLE = {
  bg: "gray.50",
  borderBottom: "1px solid",
  borderColor: "gray.200",
  color: "gray.500",
  fontSize: "13px",
  fontWeight: "600",
  textTransform: "none",
  letterSpacing: "normal",
  py: 3,
};

// Shared "Total" row look used across the platform's data tables (e.g.
// EmissionsByDivisionTable, ScopeOneDetail) — lightLime band, bold darkTeal text.
const TOTAL_CELL_STYLE = {
  px: 4,
  py: 2.5,
  fontSize: "body.s",
  fontWeight: "700",
  color: "darkTeal",
};

// Badge label/color for each context_type — shared by getContextLabel and the
// Type filter's option list.
const CONTEXT_TYPE_META = {
  task: { label: "Task", badgeColor: "purple" },
  question: { label: "Question", badgeColor: "blue" },
  report: { label: "Report", badgeColor: "green" },
  interactive: { label: "Interactive", badgeColor: "gray" },
};

// Local re-implementation of AIUsage.jsx's getContextLabel/getRoundLabel/formatDuration —
// per frontend CLAUDE.md's table-component convention, this table doesn't import from
// another page's table so a change to one never silently affects the other.
function getContextLabel(context, usageLabels) {
  const id = context.context_id || "";
  const shortId = id.substring(0, 8);
  const resolvedTitle = context.context_type === "question" ? usageLabels?.questions?.[id] : null;
  const resolvedReportTitle =
    context.context_type === "task" && context.task_type === "report_writer" ? usageLabels?.reports?.[id] : null;
  const meta = CONTEXT_TYPE_META[context.context_type];

  switch (context.context_type) {
    case "task":
      return { label: resolvedReportTitle || `Task ${shortId}`, badge: meta.label, badgeColor: meta.badgeColor };
    case "question":
      return { label: resolvedTitle || `Question ${shortId}`, badge: meta.label, badgeColor: meta.badgeColor };
    case "report":
      return { label: `Report ${shortId}`, badge: meta.label, badgeColor: meta.badgeColor };
    case "interactive":
      return { label: `Chat ${shortId}`, badge: meta.label, badgeColor: meta.badgeColor };
    default:
      return { label: shortId, badge: "Unknown", badgeColor: "gray" };
  }
}

function getRoundLabel(roundId, usageLabels) {
  if (!roundId) return "—";
  return usageLabels?.rounds?.[roundId] || roundId.substring(0, 8);
}

function getDurationMs(context) {
  if (!context.first_call || !context.last_call) return 0;
  return new Date(context.last_call) - new Date(context.first_call);
}

function formatDuration(context) {
  const diffMs = getDurationMs(context);
  if (!diffMs) return "—";
  const diffMins = Math.round(diffMs / 60000);
  if (diffMins < 60) return `${diffMins}min`;
  return `${Math.round(diffMins / 60)}h`;
}

// Placeholder "Completed By" values — no backend field for this yet, so this is
// illustrative/mock data only. Hashed off the context id (rather than row position)
// so a row's value stays the same as filtering/sorting/pagination reorders the list.
const DUMMY_COMPLETED_BY = ["admin@esg360.io", "hannah@esg360.io"];
function getDummyCompletedBy(context) {
  const id = `${context.context_type || ""}${context.context_id || ""}`;
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash += id.charCodeAt(i);
  return DUMMY_COMPLETED_BY[hash % DUMMY_COMPLETED_BY.length];
}

const SORT_COLUMNS = {
  date: { getValue: (context) => (context.first_call ? new Date(context.first_call).getTime() : 0) },
  duration: { getValue: getDurationMs },
  credits: { getValue: (context) => parseFloat(context.cost || 0) },
};

function SortIcon({ active, direction }) {
  if (!active) return <FaSort />;
  return direction === "asc" ? <FaSortUp /> : <FaSortDown />;
}

function SortableHeader({ label, sortKey, sort, onSort, isNumeric, ...rest }) {
  const active = sort.key === sortKey;
  return (
    <Th {...HEADER_CELL_STYLE} isNumeric={isNumeric} {...rest}>
      <Flex
        align="center"
        justify={isNumeric ? "flex-end" : "flex-start"}
        gap={1}
        cursor="pointer"
        onClick={() => onSort(sortKey)}
        userSelect="none"
      >
        <Box>{label}</Box>
        <SortIcon active={active} direction={sort.direction} />
      </Flex>
    </Th>
  );
}

// Multi-select filter pill — matches the platform's shared filter-bar look
// (see DimensionsGridFilterBar.jsx / SupplyChain's FilterPill.jsx): outline
// button that turns lime when a selection is active, with a count badge and
// a checkbox dropdown. `options`/`selected` are { value, label } objects.
function FilterPill({ label, options, selected, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef();
  useOutsideClick({ ref, handler: () => setIsOpen(false) });

  const hasSelection = selected.length > 0;

  return (
    <Box ref={ref} position="relative">
      <Button
        size="sm"
        borderRadius="full"
        variant="outline"
        borderColor={hasSelection ? "lime" : "gray.200"}
        bg={hasSelection ? "lightLime" : "white"}
        color="gray.700"
        fontWeight="500"
        fontSize="13px"
        rightIcon={<CaretDown size={12} />}
        onClick={() => setIsOpen(!isOpen)}
        _hover={{ borderColor: "lime" }}
      >
        {label}
        {hasSelection && (
          <Badge ml={1} borderRadius="full" fontSize="10px" colorScheme="green" variant="solid" px={1.5}>
            {selected.length}
          </Badge>
        )}
      </Button>

      {isOpen && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          zIndex={10}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="lg"
          boxShadow="md"
          minW="220px"
          maxH="260px"
          overflowY="auto"
          py={2}
          sx={{
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
            "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
            scrollbarWidth: "thin",
            scrollbarColor: "transparent transparent",
            "&:hover": { scrollbarColor: "var(--chakra-colors-gray-300) transparent" },
          }}
        >
          <VStack align="start" spacing={0} px={3}>
            {options.map((option) => (
              <Checkbox
                key={option.value}
                size="sm"
                colorScheme="limeShade"
                isChecked={selected.some((s) => s.value === option.value)}
                onChange={(e) => {
                  if (e.target.checked) {
                    onChange([...selected, option]);
                  } else {
                    onChange(selected.filter((s) => s.value !== option.value));
                  }
                }}
                py={1.5}
                w="100%"
              >
                <Text fontSize="13px" color="gray.600">
                  {option.label}
                </Text>
              </Checkbox>
            ))}
          </VStack>
        </Box>
      )}
    </Box>
  );
}

const EMPTY_FILTERS = { completedBy: [], type: [], round: [] };

// Lists the individual AI activity rows (task/question/report/chat contexts) for
// whatever period the caller has already resolved — filterable by Completed By,
// Type and Round, sortable by Date/Duration/Credits, then paginated.
function CompletedTasksTable({ rows, usageLabels, isLoading }) {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState({ key: null, direction: "asc" });

  const completedByOptions = useMemo(() => DUMMY_COMPLETED_BY.map((name) => ({ value: name, label: name })), []);

  const typeOptions = useMemo(() => {
    const types = new Set((rows || []).map((r) => r.context_type).filter(Boolean));
    return Array.from(types).map((type) => ({ value: type, label: CONTEXT_TYPE_META[type]?.label || type }));
  }, [rows]);

  const roundOptions = useMemo(() => {
    const roundIds = new Set((rows || []).map((r) => r.round_id).filter(Boolean));
    return Array.from(roundIds).map((roundId) => ({ value: roundId, label: getRoundLabel(roundId, usageLabels) }));
  }, [rows, usageLabels]);

  const filteredRows = useMemo(() => {
    return (rows || []).filter((context) => {
      if (filters.completedBy.length && !filters.completedBy.some((f) => f.value === getDummyCompletedBy(context))) {
        return false;
      }
      if (filters.type.length && !filters.type.some((f) => f.value === context.context_type)) return false;
      if (filters.round.length && !filters.round.some((f) => f.value === context.round_id)) return false;
      return true;
    });
  }, [rows, filters]);

  const sortedRows = useMemo(() => {
    if (!sort.key) return filteredRows;
    const { getValue } = SORT_COLUMNS[sort.key];
    const sorted = [...filteredRows].sort((a, b) => getValue(a) - getValue(b));
    return sort.direction === "asc" ? sorted : sorted.reverse();
  }, [filteredRows, sort]);

  const handleSort = (key) => {
    setSort((prev) =>
      prev.key === key ? { key, direction: prev.direction === "asc" ? "desc" : "asc" } : { key, direction: "asc" }
    );
    setPageIndex(0);
  };

  const handleFilterChange = (key) => (next) => {
    setFilters((prev) => ({ ...prev, [key]: next }));
    setPageIndex(0);
  };

  if (isLoading) {
    return (
      <Flex justify="center" py={4}>
        <Spinner />
      </Flex>
    );
  }

  if (!rows?.length) {
    return (
      <Text fontSize="sm" color="gray.500">
        No AI tasks completed in this period.
      </Text>
    );
  }

  const totalCount = sortedRows.length;
  const numberOfPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePageIndex = Math.min(pageIndex, numberOfPages - 1);
  const pageRows = sortedRows.slice(safePageIndex * pageSize, safePageIndex * pageSize + pageSize);
  // Reflects the currently filtered set, not the whole period.
  const totalCredits = Math.ceil(sortedRows.reduce((sum, r) => sum + parseFloat(r.cost || 0), 0));
  const activeFilterCount = filters.completedBy.length + filters.type.length + filters.round.length;

  return (
    <>
      <Flex gap={2} align="center" flexWrap="wrap" mb={3}>
        <FilterPill
          label="Completed By"
          options={completedByOptions}
          selected={filters.completedBy}
          onChange={handleFilterChange("completedBy")}
        />
        <FilterPill label="Type" options={typeOptions} selected={filters.type} onChange={handleFilterChange("type")} />
        <FilterPill
          label="Round"
          options={roundOptions}
          selected={filters.round}
          onChange={handleFilterChange("round")}
        />
        {activeFilterCount > 0 && (
          <Text
            as="button"
            fontSize="13px"
            color="limeShade.600"
            fontWeight="500"
            _hover={{ color: "limeShade.700" }}
            onClick={() => setFilters(EMPTY_FILTERS)}
          >
            Clear filters
          </Text>
        )}
        {activeFilterCount > 0 && (
          <Text fontSize="13px" color="gray.500">
            {sortedRows.length} of {rows.length} items
          </Text>
        )}
      </Flex>

      {sortedRows.length === 0 ? (
        <Text fontSize="sm" color="gray.500">
          No AI tasks match these filters.
        </Text>
      ) : (
        <>
          <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <SortableHeader label="Date" sortKey="date" sort={sort} onSort={handleSort} />
                  <Th {...HEADER_CELL_STYLE}>Completed By</Th>
                  <Th {...HEADER_CELL_STYLE}>Type</Th>
                  <Th {...HEADER_CELL_STYLE}>Round</Th>
                  <Th {...HEADER_CELL_STYLE}>Context</Th>
                  <SortableHeader label="Duration" sortKey="duration" sort={sort} onSort={handleSort} />
                  <SortableHeader label="Credits" sortKey="credits" sort={sort} onSort={handleSort} />
                </Tr>
              </Thead>
              <Tbody>
                {pageRows.map((context, i) => {
                  const { label, badge, badgeColor } = getContextLabel(context, usageLabels);
                  return (
                    <Tr key={`${context.context_type}-${context.context_id}-${i}`}>
                      <Td fontSize="xs">
                        {context.first_call ? new Date(context.first_call).toLocaleDateString() : "—"}
                      </Td>
                      <Td>
                        <Badge
                          px={2}
                          py={1}
                          borderRadius="md"
                          fontSize="body.xxs"
                          colorScheme="orange"
                          width="fit-content"
                        >
                          {getDummyCompletedBy(context)}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge colorScheme={badgeColor}>{badge}</Badge>
                        {context.task_type && (
                          <Text fontSize="xs" color="gray.500">
                            {context.task_type}
                          </Text>
                        )}
                      </Td>
                      <Td fontSize="xs">{getRoundLabel(context.round_id, usageLabels)}</Td>
                      <Td fontFamily="mono" fontSize="xs">
                        {label}
                      </Td>
                      <Td fontSize="xs">{formatDuration(context)}</Td>
                      <Td fontSize="xs">{Math.ceil(parseFloat(context.cost || 0))}</Td>
                    </Tr>
                  );
                })}
              </Tbody>
              <Tfoot>
                <Tr bg="lightLime" borderTop="2px solid" borderColor="gray.200">
                  <Td colSpan={6} {...TOTAL_CELL_STYLE}>
                    Total
                  </Td>
                  <Td {...TOTAL_CELL_STYLE}>{totalCredits}</Td>
                </Tr>
              </Tfoot>
            </Table>
          </Box>
          <Flex justify="center" mt={4}>
            <Paginator
              selectedPage={safePageIndex + 1}
              numberOfPages={numberOfPages}
              handlePageSelection={(newPage) => setPageIndex(newPage - 1)}
              pageSize={pageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onPageSizeChange={(newSize) => {
                setPageSize(newSize);
                setPageIndex(0);
              }}
              totalCount={totalCount}
            />
          </Flex>
        </>
      )}
    </>
  );
}

export default CompletedTasksTable;
